import React, { useState, useEffect } from "react";
import { BrowserRouter as Router, Routes, Route, Navigate } from "react-router-dom";

// Sayfalar ve Bileşenler
import Home from "./pages/Home";
import Landing from "./pages/Landing";
import Sohbet from "./pages/Sohbet";
import Header from "./components/Header";
import Sidebar from "./components/Sidebar";
import Footer from "./components/Footer";
import Code from "./pages/Code";
import Kaydedilenler from "./pages/Kaydedilenler";
import Profile from './components/Profile';
import Login from './components/Login';
import Register from './components/Register';
import PostDetail from './components/PostDetail';
import Notifications from './pages/Notifications';
import SohbetDetail from './components/SohbetDetail';
import API, { tokenStorage } from './services/api';
import { fetchCurrentUser, isAuthenticated, logout as clearSession } from './services/authService';
import { io } from 'socket.io-client';

// Yetkisiz kullanıcıların korumalı sayfalara girmesini engelleyen sarmalayıcı bileşen
const ProtectedRoute = ({ user, loading, children }) => {
  if (loading) {
    return <div style={{ display: 'flex', justifyContent: 'center', padding: '50px' }}>Yükleniyor...</div>;
  }
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  return children;
};

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [savedPosts, setSavedPosts] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [posts, setPosts] = useState([]);
  const [postsLoading, setPostsLoading] = useState(true);
  const [genelCategoryId, setGenelCategoryId] = useState(null);

  // "Genel" kategorisinin gerçek id'sini bir kere çekiyoruz — daha önce
  // sabit "1" varsayılıyordu, backend'in seed sırası değişirse kırılırdı.
  useEffect(() => {
    API.get('/categories/genel')
      .then(res => setGenelCategoryId(res.data?.id))
      .catch(() => setGenelCategoryId(null));
  }, []);

  // 1. Uygulama Başladığında Oturum ve Profil Doğrulaması (JWT Auth)
  useEffect(() => {
    const checkAuth = async () => {
      // Not: token'lar authService/tokenStorage tarafından 'ebmt_access_token' /
      // 'ebmt_refresh_token' anahtarlarıyla saklanıyor — burada eskiden 'token'
      // anahtarına bakılıyordu, bu yüzden geçerli bir oturum bile sürekli
      // "yok" gibi görünüyordu.
      if (!isAuthenticated()) {
        setUser(null);
        localStorage.removeItem("user");
        setLoading(false);
        return;
      }

      try {
        // Token geçerliliğini backend'den kontrol et
        const freshUser = await fetchCurrentUser();
        setUser(freshUser);
        localStorage.setItem("user", JSON.stringify(freshUser));
      } catch (error) {
        // Token geçersiz/süresi dolmuş ve refresh de başarısız oldu
        clearSession();
        localStorage.removeItem("user");
        setUser(null);
      } finally {
        setLoading(false);
      }
    };

    checkAuth();
  }, []);

  // 2. Bildirimleri API'den Çekme
  useEffect(() => {
    if (user) {
      API.get('/notifications')
        .then(res => {
          // Backend read_at (timestamp|null) döner, UI isRead (boolean) bekliyor.
          // Backend type: 'comment_on_post' / 'vote_received' -> UI: 'reply' / 'like'.
          // postId/commentId, backend'in trigger'larda doldurduğu data (jsonb)
          // alanının içinde geliyor (bkz. week5/week5b sql dosyaları).
          const normalized = (res.data || []).map(n => {
            const uiType = n.type === 'comment_on_post' ? 'reply'
              : n.type === 'vote_received' ? 'like'
              : n.type;
            const postId = n.data?.post_id
              || (n.data?.target_type === 'post' ? n.data?.target_id : undefined);
            return {
              ...n,
              type: uiType,
              text: n.body,
              postId,
              commentId: n.data?.comment_id,
              isRead: Boolean(n.read_at),
              is_read: Boolean(n.read_at)
            };
          });
          setNotifications(normalized);
        })
        .catch(() => setNotifications([]));
    }
  }, [user]);

  // Real-time bildirim (socket.io) — backend user_<uuid> odasına JWT ile
// otomatik katıyor, 'notification' event'i ile anlık push atıyor.
useEffect(() => {
  if (!user) return;

  const accessToken = tokenStorage.getAccessToken();
  if (!accessToken) return;

  const socketBaseUrl = (process.env.REACT_APP_API_URL || 'http://localhost:3000/api').replace(/\/api\/?$/, '');
  const socket = io(socketBaseUrl, {
    auth: { token: accessToken }
  });

  socket.on('notification', (payload) => {
    const uiType = payload.type === 'comment_on_post' ? 'reply'
      : payload.type === 'vote_received' ? 'like'
      : payload.type;

    const text = payload.type === 'comment_on_post'
      ? 'Gönderine yeni bir yorum geldi.'
      : payload.type === 'vote_received'
      ? 'Bir içeriğine oy geldi.'
      : 'Yeni bildirim.';

    const newNotification = {
      id: `live-${Date.now()}`,
      type: uiType,
      text,
      postId: payload.post_id,
      commentId: payload.comment_id,
      isRead: false,
      is_read: false,
      created_at: new Date().toISOString()
    };

    setNotifications(prev => [newNotification, ...prev]);
  });

  socket.on('connect_error', (err) => {
    console.warn('Socket bağlantı hatası:', err.message);
  });

  return () => {
    socket.disconnect();
  };
}, [user]);

  // 3. Gönderileri (posts) API'den çekme — daha önce Home hep boş görünüyordu
  // çünkü bu istek hiç atılmıyordu.
  useEffect(() => {
    if (!user) {
      setPosts([]);
      setPostsLoading(false);
      return;
    }
    setPostsLoading(true);
    // NOT: backend artık GET /posts'ta sayfalama yapıyor (varsayılan limit=10).
    // Gerçek "daha fazla yükle" UI'ı henüz yok, bu yüzden şimdilik daha
    // yüksek bir limit istiyoruz — kalıcı çözüm değil, ileride sayfalama
    // UI'ı eklenmeli.
    API.get('/posts?limit=100')
      .then(res => setPosts(res.data || []))
      .catch(() => setPosts([]))
      .finally(() => setPostsLoading(false));
  }, [user]);

  // Yeni gönderi oluşturma (questionbox.js / CodeSoruSor.js buradan çağırır)
  // NOT: backend categoryId'yi zorunlu tutuyor; kategori seçimi UI'a henüz
  // eklenmediği için şimdilik "Genel" (id: 1) sabit gönderiliyor — kategori
  // konusu netleşince burası gerçek bir seçim alanına bağlanmalı.
  const addPost = async (newPost) => {
  const payload = {
    title: newPost.title,
    content: newPost.description || newPost.content || '',
    categoryId: newPost.categoryId || genelCategoryId || 1,
    tags: newPost.tags || []
  };

    const createRes = await API.post('/posts', payload);
    const newPostId = createRes.data?.postId;

    // create_post_with_tags RPC sadece yeni postun id'sini döner,
    // ekranda göstermek için tam post detayını ayrıca çekiyoruz.
    const detailRes = await API.get(`/posts/${newPostId}`);
    setPosts(prev => [detailRes.data, ...prev]);
    return detailRes.data;
  };

  // 4. Kaydedilmiş (favori) gönderileri backend'den yükleme — daha önce
  // sayfa yenilenince/oturum açılınca hiç çekilmiyordu, hep boş kalıyordu.
  useEffect(() => {
    if (!user) {
      setSavedPosts([]);
      return;
    }
    API.get('/favorites')
      .then(res => setSavedPosts(res.data || []))
      .catch(() => setSavedPosts([]));
  }, [user]);

  // Bir postun yorum sayısını hem PostDetail'de hem Home listesinde (App.js
  // seviyesindeki genel `posts` state'inde) güncel tutar.
  const updatePostInList = (postId, changes) => {
    setPosts(prev => prev.map(p => p.id === postId ? { ...p, ...changes } : p));
  };

  // Kendi postunu silme
  const deletePost = async (postId) => {
    await API.delete(`/posts/${postId}`);
    setPosts(prev => prev.filter(p => p.id !== postId));
    setSavedPosts(prev => prev.filter(p => p.id !== postId));
  };

  // Avatar güncellenince hem user state'inde hem localStorage'da yansıt
  const updateUserAvatar = (newAvatarUrl) => {
    setUser(prev => {
      const updated = { ...prev, avatar: newAvatarUrl };
      localStorage.setItem('user', JSON.stringify(updated));
      return updated;
    });
  };
  // Kaydedilen gönderi ekleme/çıkarma
  // NOT: backend'de "kaydet" endpoint'i /posts/:id/save değil /api/favorites'tir.
  const toggleSavePost = async (post) => {
    try {
      const isAlreadySaved = savedPosts.some((p) => p.id === post.id);
      if (isAlreadySaved) {
        await API.delete(`/favorites/${post.id}`);
        setSavedPosts(prev => prev.filter((p) => p.id !== post.id));
      } else {
        await API.post('/favorites', { postId: post.id });
        setSavedPosts(prev => [post, ...prev]);
      }
    } catch (err) {
      // Fallback UI State
      setSavedPosts(prev => {
        const isAlreadySaved = prev.some((p) => p.id === post.id);
        return isAlreadySaved ? prev.filter((p) => p.id !== post.id) : [post, ...prev];
      });
    }
  };

  // NOT: backend bu iki uçta POST değil PATCH bekliyor.
  const markAllAsRead = async () => {
    try {
      await API.patch('/notifications/read-all');
      setNotifications(prev => prev.map(n => ({ ...n, isRead: true, is_read: true })));
    } catch (err) {
      setNotifications(prev => prev.map(n => ({ ...n, isRead: true, is_read: true })));
    }
  };

  const markAsRead = async (id) => {
    try {
      await API.patch(`/notifications/${id}/read`);
      setNotifications(prev =>
        prev.map(n => n.id === id ? { ...n, isRead: true, is_read: true } : n)
      );
    } catch (err) {
      setNotifications(prev =>
        prev.map(n => n.id === id ? { ...n, isRead: true, is_read: true } : n)
      );
    }
  };

  return (
    <Router>
      <div className="App">
        <Header
          user={user}
          setUser={setUser}
          searchQuery={searchQuery}
          setSearchQuery={setSearchQuery}
          unreadCount={notifications.filter(n => !(n.isRead || n.is_read)).length}
          notifications={notifications}
          onMenuToggle={() => setIsSidebarOpen(prev => !prev)}
        />
        <div className="main-container">
          <Sidebar
            user={user}
            setUser={setUser}
            isOpen={isSidebarOpen}
            onClose={() => setIsSidebarOpen(false)}
          />
          <div className="content">
            <Routes>
              {/* Korumalı Rotalar */}
              {/* "/" artık korumalı değil: giriş yapılmamışsa herkese açık
    Landing sayfası, giriş yapılmışsa normal Home gösterilir. */}
              <Route
                path="/"
                element={
                  loading ? (
                    <div style={{ display: 'flex', justifyContent: 'center', padding: '50px' }}>Yükleniyor...</div>
                  ) : user ? (
                    <Home
                      searchQuery={searchQuery}
                      toggleSavePost={toggleSavePost}
                      savedPosts={savedPosts}
                      user={user}
                      posts={posts}
                      addPost={addPost}
                      postsLoading={postsLoading}
                    />
                  ) : (
                    <Landing />
                  )
                }
              />
              <Route 
                path="/Code" 
                element={
                  <ProtectedRoute user={user} loading={loading}>
                    <Code user={user} posts={posts} addPost={addPost} />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/Sohbet" 
                element={
                  <ProtectedRoute user={user} loading={loading}>
                    <Sohbet user={user} toggleSavePost={toggleSavePost} savedPosts={savedPosts} />
                  </ProtectedRoute>
                } 
              />
              <Route
                path="/Kaydedilenler"
                element={
                  <ProtectedRoute user={user} loading={loading}>
                    <Kaydedilenler
                      savedPosts={savedPosts}
                      toggleSavePost={toggleSavePost}
                    />
                  </ProtectedRoute>
                }
              />
              <Route 
                path="/post/:id" 
                element={
                  <ProtectedRoute user={user} loading={loading}>
                    <PostDetail user={user} onCommentCountChange={updatePostInList} deletePost={deletePost} />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/sohbet/:id" 
                element={
                  <ProtectedRoute user={user} loading={loading}>
                    <SohbetDetail user={user} />
                  </ProtectedRoute>
                } 
              />
              <Route
                path="/notifications"
                element={
                  <ProtectedRoute user={user} loading={loading}>
                    <Notifications
                      notifications={notifications}
                      markAllAsRead={markAllAsRead}
                      markAsRead={markAsRead}
                    />
                  </ProtectedRoute>
                }
              />
              <Route 
                path="/profile" 
                element={
                  <ProtectedRoute user={user} loading={loading}>
                    <Profile user={user} posts={posts} savedPosts={savedPosts} onAvatarUpdate={updateUserAvatar} />
                  </ProtectedRoute>
                } 
              />
              {/* Küçük/Büyük harf uyumu için takma ad (alias) rota */}
              <Route path="/Profil" element={<Navigate to="/profile" replace />} />

              {/* Giriş & Kayıt Rotaları */}
              <Route path="/login" element={!user ? <Login setUser={setUser} /> : <Navigate to="/" replace />} />
              <Route path="/register" element={!user ? <Register setUser={setUser} /> : <Navigate to="/" replace />} />

              {/* Tanımsız URL yönlendirmesi */}
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </div>
        </div>
        <Footer />
      </div>
    </Router>
  );
}

export default App;